// Free public calculator definitions. Each calc is data + a pure compute() fn.
// Rendered by Calculators.js. No login, no backend — pure client-side SEO tools.

const money = (n) =>
  isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";
const num = (n, d = 2) => (isFinite(n) ? Number(n).toFixed(d) : "—");

export const CALCS = [
  {
    slug: "gmroi-calculator",
    title: "GMROI Calculator",
    h1: "GMROI Calculator",
    category: "Inventory & Margin",
    description:
      "Free GMROI calculator: enter revenue, gross margin and average inventory cost to get Gross Margin Return on Investment instantly — plus what a healthy GMROI looks like.",
    keywords: "GMROI calculator, gross margin return on investment, GMROI formula, inventory profitability calculator, retail GMROI benchmark",
    intro:
      "GMROI (Gross Margin Return on Investment) tells you how many gross-margin dollars you earn for every dollar tied up in inventory. Above ~3.0 is healthy for most retail; below 1.0 means the inventory is losing money.",
    inputs: [
      { key: "revenue", label: "Annual revenue", prefix: "$", default: 500000 },
      { key: "margin", label: "Gross margin", suffix: "%", default: 30 },
      { key: "avgInv", label: "Average inventory (at cost)", prefix: "$", default: 90000 },
    ],
    compute: (v) => {
      const gp = v.revenue * (v.margin / 100);
      const gmroi = v.avgInv > 0 ? gp / v.avgInv : NaN;
      const verdict =
        gmroi >= 3.5 ? { t: "Excellent", c: "emerald" }
        : gmroi >= 2 ? { t: "Healthy", c: "emerald" }
        : gmroi >= 1 ? { t: "Marginal — room to improve", c: "amber" }
        : { t: "Losing money on inventory", c: "rose" };
      return {
        headline: num(gmroi, 2),
        headlineLabel: "GMROI",
        verdict,
        rows: [
          ["Gross profit", money(gp)],
          ["Average inventory (cost)", money(v.avgInv)],
          ["GMROI", `${num(gmroi, 2)} : 1`],
          ["Gross margin $ per $1 of inventory", money(gmroi)],
        ],
      };
    },
    formula: "GMROI = Gross Profit $ ÷ Average Inventory Cost",
    explainer: [
      { t: "h2", c: "What is a good GMROI?" },
      { t: "p", c: "There's no universal number — it varies by category and turn rate — but a rule of thumb for general retail is that a GMROI above 3.0 is strong, 2.0–3.0 is solid, and anything under 1.0 means that inventory is generating less gross margin than the cash it consumes. High-turn, low-margin categories (like grocery staples) and low-turn, high-margin categories (like specialty goods) can both hit healthy GMROI by different routes." },
      { t: "h2", c: "How to improve GMROI" },
      { t: "ul", c: [
        "Raise margin — negotiate cost, fix underpriced items, trim discounting on hero products.",
        "Turn faster — cut weeks-of-supply on slow movers so less cash sits in stock.",
        "Rationalize the tail — delist dead SKUs that drag average inventory up without adding margin.",
      ] },
    ],
  },
  {
    slug: "inventory-turnover-calculator",
    title: "Inventory Turnover Calculator",
    h1: "Inventory Turnover (Turns) Calculator",
    category: "Inventory & Margin",
    description:
      "Free inventory turnover calculator: enter COGS and average inventory to get your inventory turns and days of supply, with benchmarks for retail and grocery.",
    keywords: "inventory turnover calculator, inventory turns calculator, days of supply calculator, stock turn ratio, inventory turnover formula",
    intro:
      "Inventory turnover shows how many times you sell and replace your average stock over a year. Higher turns free cash and cut spoilage; too high can mean stockouts.",
    inputs: [
      { key: "cogs", label: "Annual cost of goods sold (COGS)", prefix: "$", default: 350000 },
      { key: "avgInv", label: "Average inventory (at cost)", prefix: "$", default: 45000 },
    ],
    compute: (v) => {
      const turns = v.avgInv > 0 ? v.cogs / v.avgInv : NaN;
      const days = isFinite(turns) && turns > 0 ? 365 / turns : NaN;
      const verdict =
        turns >= 12 ? { t: "Very fast — watch for stockouts", c: "amber" }
        : turns >= 6 ? { t: "Healthy for most retail", c: "emerald" }
        : turns >= 3 ? { t: "Slow — capital is tied up", c: "amber" }
        : { t: "Very slow — dead-stock risk", c: "rose" };
      return {
        headline: num(turns, 1),
        headlineLabel: "Turns / year",
        verdict,
        rows: [
          ["Inventory turns", `${num(turns, 1)}×`],
          ["Days of supply", `${num(days, 0)} days`],
          ["Weeks of supply", `${num(days / 7, 1)} weeks`],
        ],
      };
    },
    formula: "Turns = Annual COGS ÷ Average Inventory (at cost)  ·  Days of Supply = 365 ÷ Turns",
    explainer: [
      { t: "h2", c: "What is a good inventory turnover?" },
      { t: "p", c: "It depends heavily on category. Fresh grocery might turn 20+ times a year; general merchandise 4–8; big-ticket or specialty goods 2–4. The point isn't to maximize turns blindly — it's to turn fast enough to keep cash moving and product fresh, without turning so fast you run out and lose sales." },
      { t: "h2", c: "Turns, cash and dead stock" },
      { t: "p", c: "Low turns are the fingerprint of dead inventory: cash trapped in product that isn't moving. Track weeks-of-supply per SKU, base reorders on real velocity, and clear aged stock with a markdown ladder to lift turns where it matters." },
    ],
  },
  {
    slug: "weeks-of-supply-calculator",
    title: "Weeks of Supply Calculator",
    h1: "Weeks of Supply Calculator",
    category: "Inventory & Margin",
    description:
      "Free weeks-of-supply calculator: enter on-hand units and average weekly sales to see how long your current stock will last — and whether you're over- or under-stocked.",
    keywords: "weeks of supply calculator, weeks of supply formula, days of supply calculator, WOS retail, how long will inventory last",
    intro:
      "Weeks of Supply (WOS) is how many weeks your current on-hand stock will last at recent sales pace. It's the fastest read on whether an item is over- or under-stocked.",
    inputs: [
      { key: "onhand", label: "On-hand units", default: 480 },
      { key: "weekly", label: "Average weekly units sold", default: 60 },
      { key: "target", label: "Target weeks of supply (optional)", default: 4 },
    ],
    compute: (v) => {
      const wos = v.weekly > 0 ? v.onhand / v.weekly : NaN;
      const overUnder = isFinite(wos) ? wos - v.target : NaN;
      const excessUnits = overUnder > 0 ? Math.round(overUnder * v.weekly) : 0;
      const verdict =
        !isFinite(wos) ? { t: "Enter weekly sales", c: "amber" }
        : wos > v.target * 1.5 ? { t: "Overstocked — cash tied up", c: "rose" }
        : wos < 1 ? { t: "Stockout risk — reorder now", c: "rose" }
        : wos < v.target ? { t: "Below target — plan a reorder", c: "amber" }
        : { t: "Well-stocked", c: "emerald" };
      return {
        headline: num(wos, 1),
        headlineLabel: "Weeks of supply",
        verdict,
        rows: [
          ["Weeks of supply", `${num(wos, 1)} weeks`],
          ["Days of supply", `${num(wos * 7, 0)} days`],
          ["vs. target", `${overUnder >= 0 ? "+" : ""}${num(overUnder, 1)} weeks`],
          ["Excess units above target", `${excessUnits.toLocaleString()} units`],
        ],
      };
    },
    formula: "Weeks of Supply = On-hand Units ÷ Average Weekly Units Sold",
    explainer: [
      { t: "h2", c: "How to use weeks of supply" },
      { t: "p", c: "WOS turns a raw inventory number into a decision. Set a target band per category (say 3–6 weeks). Items far above the band are overstocked — candidates for markdown, bundling or slower reordering. Items under 1–2 weeks are stockout risks that need a reorder now. It's the same signal that flags both dead stock and lost sales." },
      { t: "h2", c: "Set targets by lead time" },
      { t: "p", c: "Your target WOS should cover supplier lead time plus a safety buffer. If a vendor takes two weeks to deliver, a one-week target guarantees stockouts. Tie the target to real lead times and velocity, not a single number across the store." },
    ],
  },
  {
    slug: "grain-storage-roi-calculator",
    title: "Grain Storage ROI Calculator",
    h1: "Grain Storage ROI Calculator — Store or Sell?",
    category: "Grain Marketing",
    description:
      "Free grain storage ROI calculator: enter your harvest price, expected later price, storage cost and interest to see the net per-bushel gain of storing vs. selling now.",
    keywords: "grain storage ROI calculator, store or sell grain calculator, is it worth storing grain, grain storage cost calculator, corn storage calculator, sell or store corn",
    intro:
      "Storing grain only pays if the expected price gain clears your true cost to carry — storage, interest and shrink. Run your numbers below to see the net per bushel and whether storing beats selling at harvest.",
    inputs: [
      { key: "bushels", label: "Bushels to store", default: 20000 },
      { key: "harvest", label: "Harvest cash price", prefix: "$", suffix: "/bu", default: 4.20 },
      { key: "expected", label: "Expected later cash price", prefix: "$", suffix: "/bu", default: 4.65 },
      { key: "months", label: "Months to store", default: 6 },
      { key: "storage", label: "Storage cost", prefix: "$", suffix: "/bu/mo", default: 0.03 },
      { key: "interest", label: "Interest rate", suffix: "%/yr", default: 8 },
      { key: "shrink", label: "Shrink & quality loss", suffix: "%", default: 1 },
    ],
    compute: (v) => {
      const grossGain = v.expected - v.harvest; // $/bu
      const storageCost = v.storage * v.months; // $/bu
      const interestCost = v.harvest * (v.interest / 100) * (v.months / 12); // $/bu
      const shrinkCost = v.expected * (v.shrink / 100); // $/bu
      const carry = storageCost + interestCost + shrinkCost;
      const netPerBu = grossGain - carry;
      const totalNet = netPerBu * v.bushels;
      const breakeven = v.harvest + carry; // price needed to break even
      const verdict =
        netPerBu > 0.05 ? { t: "Storing pays — hedge to lock it", c: "emerald" }
        : netPerBu >= -0.02 ? { t: "Roughly break-even — sell unless bins are free", c: "amber" }
        : { t: "Storing loses money — sell at harvest", c: "rose" };
      return {
        headline: `${netPerBu >= 0 ? "+" : ""}$${num(netPerBu, 3)}`,
        headlineLabel: "Net per bushel from storing",
        verdict,
        rows: [
          ["Gross price gain", `$${num(grossGain, 3)}/bu`],
          ["Storage cost", `−$${num(storageCost, 3)}/bu`],
          ["Interest on value", `−$${num(interestCost, 3)}/bu`],
          ["Shrink & quality", `−$${num(shrinkCost, 3)}/bu`],
          ["Net per bushel", `${netPerBu >= 0 ? "+" : "−"}$${num(Math.abs(netPerBu), 3)}/bu`],
          ["Break-even later price", `$${num(breakeven, 3)}/bu`],
          ["Total net on your bushels", money(totalNet)],
        ],
      };
    },
    formula:
      "Net/bu = (Expected price − Harvest price) − Storage×Months − Harvest×Interest×(Months÷12) − Shrink",
    explainer: [
      { t: "h2", c: "How to read the result" },
      { t: "p", c: "If the net per bushel is positive, the market is paying you to store — but only lock that gain in by hedging (forward contract, futures or HTA), because an unpriced bin still carries price risk. If it's near zero, storing is a gamble that rarely beats taking the cash. If it's negative, the expected gain doesn't cover your carry and you should sell at harvest." },
      { t: "h2", c: "Don't forget basis" },
      { t: "p", c: "Your 'expected later price' should reflect both the futures move and your local basis. A weak harvest basis that strengthens into spring is often the real reason storage pays — the futures may barely move. Track your local basis against its history before you decide." },
      { t: "h2", c: "Let AssortIQ run it per commodity" },
      { t: "p", c: "AssortIQ's Basis & Storage Advisor does this calculation automatically for every commodity you grow — using live futures, your modeled basis and carry cost — and returns a clear Sell / Store / Hedge call with per-facility weather risk. Preview the live agriculture demo, free." },
    ],
  },
  {
    slug: "attach-rate-calculator",
    title: "Accessory Attach-Rate Calculator",
    h1: "Accessory Attach-Rate Calculator",
    category: "Retail & Attach",
    description:
      "Free accessory attach-rate calculator: enter device sales and accessory units to get your attach rate — plus the extra margin you'd earn by hitting a target attach rate.",
    keywords: "attach rate calculator, accessory attach rate, attach rate formula, cellular accessory attach, upsell rate calculator, how to calculate attach rate",
    intro:
      "Attach rate is how many accessories (or add-ons) you sell per primary product — the number that decides how much margin a device sale really makes. Enter your numbers to see where you stand and what a higher attach rate is worth.",
    inputs: [
      { key: "devices", label: "Primary units sold (e.g. phones/month)", default: 300 },
      { key: "accessories", label: "Accessory units sold", default: 210 },
      { key: "accMargin", label: "Avg margin per accessory", prefix: "$", default: 14 },
      { key: "target", label: "Target attach rate", suffix: "%", default: 120 },
    ],
    compute: (v) => {
      const rate = v.devices > 0 ? (v.accessories / v.devices) * 100 : NaN;
      const targetUnits = v.devices * (v.target / 100);
      const gapUnits = Math.max(0, targetUnits - v.accessories);
      const upside = gapUnits * v.accMargin;
      const perDevice = v.devices > 0 ? (v.accessories * v.accMargin) / v.devices : NaN;
      const verdict =
        !isFinite(rate) ? { t: "Enter device sales", c: "amber" }
        : rate >= v.target ? { t: "At or above target — strong attach", c: "emerald" }
        : rate >= 100 ? { t: "Solid — room to push higher", c: "amber" }
        : { t: "Low attach — big margin left on the table", c: "rose" };
      return {
        headline: `${num(rate, 0)}%`,
        headlineLabel: "Current attach rate",
        verdict,
        rows: [
          ["Attach rate", `${num(rate, 0)}%`],
          ["Accessory margin per device", money(perDevice)],
          ["Units to reach target", `${Math.round(gapUnits).toLocaleString()} units`],
          ["Extra monthly margin at target", money(upside)],
          ["Extra annual margin at target", money(upside * 12)],
        ],
      };
    },
    formula: "Attach rate = Accessory Units ÷ Primary Units × 100  ·  Upside = (Target − Current units) × Accessory Margin",
    explainer: [
      { t: "h2", c: "What is a good attach rate?" },
      { t: "p", c: "In wireless and electronics retail, a healthy accessory attach rate is often well above 100% — meaning more than one accessory per device (a case AND a screen protector AND a charger). Under 100% usually signals a big, easy margin opportunity: the traffic is already in the store buying the device, so every extra add-on is high-margin incremental revenue." },
      { t: "h2", c: "How to lift attach rate" },
      { t: "ul", c: [
        "Bundle the obvious pairs — the case, protector and charger that go with each device.",
        "Show attach at the point of sale, not as an afterthought at checkout.",
        "Track attach per device and per store so you can coach the locations that lag.",
      ] },
      { t: "h2", c: "Let AssortIQ find the best pairs" },
      { t: "p", c: "AssortIQ's opportunity discovery surfaces which accessories attach best to each device and which stores are under-attaching — so you push the right add-ons where they'll actually sell. See it on a live cellular or electronics demo, free." },
    ],
  },
];

export const getCalc = (slug) => CALCS.find((c) => c.slug === slug);
